import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";

const allowE2EAuthBypass =
  import.meta.env.MODE !== "production" &&
  import.meta.env.VITE_DANGEROUS_E2E_BYPASS_AUTH === "true";

function isAllowedCliCallbackUrl(value: string): boolean {
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return false;
  }

  if (parsed.protocol !== "http:") {
    return false;
  }

  if (parsed.username || parsed.password) {
    return false;
  }

  return parsed.hostname === "127.0.0.1" || parsed.hostname === "localhost";
}
export default function AuthGate({ children }: { children: React.ReactNode }) {
  const [ready, setReady] = useState(false);
  const [email, setEmail] = useState("");
  const [session, setSession] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [cliRedirecting, setCliRedirecting] = useState(false);
  const bypassForE2E = allowE2EAuthBypass;
  const navigate = useNavigate();

  useEffect(() => {
    if (bypassForE2E) {
      setReady(true);
      return;
    }
    let active = true;
    const load = async () => {
      const { data } = await supabase.auth.getSession();
      if (!active) return;
      setSession(data.session?.access_token ?? null);
      setReady(true);
    };
    void load();
    const { data } = supabase.auth.onAuthStateChange((event, next) => {
      if (!active) return;
      setSession(next?.access_token ?? null);
      if (
        event === "SIGNED_IN" &&
        !new URLSearchParams(window.location.search).get("cli_callback") &&
        !localStorage.getItem("passiveagents.setup-shown")
      ) {
        localStorage.setItem("passiveagents.setup-shown", "1");
        navigate("/docs?tab=setup", { replace: true });
      }
    });
    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, [bypassForE2E, navigate]);

  useEffect(() => {
    if (bypassForE2E) {
      return;
    }
    const redirect = async () => {
      const cliCallback = new URLSearchParams(window.location.search).get(
        "cli_callback",
      );
      if (!cliCallback || !session) return;

      if (!isAllowedCliCallbackUrl(cliCallback)) {
        setMessage("Invalid CLI callback URL.");
        return;
      }

      const { data } = await supabase.auth.getSession();
      const next = data.session;
      if (!next?.access_token || !next?.refresh_token || !next.expires_at) {
        setMessage("Unable to get active session for CLI login.");
        return;
      }

      setCliRedirecting(true);
      const callback = new URL(cliCallback);
      callback.searchParams.set("access_token", next.access_token);
      callback.searchParams.set("refresh_token", next.refresh_token);
      callback.searchParams.set("expires_at", String(next.expires_at));
      if (next.user?.email) {
        callback.searchParams.set("email", next.user.email);
      }
      window.location.href = callback.toString();
    };

    void redirect();
  }, [session, bypassForE2E]);

  if (bypassForE2E) {
    return <>{children}</>;
  }

  const sendMagicLink = async () => {
    if (!email.trim()) return;
    setMessage(null);
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: window.location.origin },
    });
    if (error) {
      setMessage(error.message);
      return;
    }
    setMessage("Check your email for a sign-in link.");
  };

  const signInWithOAuth = async (provider: "github" | "google") => {
    setMessage(null);
    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: window.location.origin },
    });
    if (error) {
      setMessage(error.message);
    }
  };

  if (!ready) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        Loading...
      </div>
    );
  }

  if (!session) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="w-full max-w-sm space-y-4 rounded-xl border border-border bg-card p-6">
          <div>
            <h1 className="font-mono text-sm font-semibold uppercase tracking-widest text-muted-foreground">
              PassiveAgents Login
            </h1>
            <p className="text-xs text-muted-foreground mt-2">
              Sign in with GitHub, Google, or email to access your workspace.
            </p>
          </div>
          <div className="space-y-2">
            <button
              onClick={() => void signInWithOAuth("github")}
              className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm font-medium text-foreground hover:bg-secondary/80 transition-colors"
            >
              Continue with GitHub
            </button>
            <button
              onClick={() => void signInWithOAuth("google")}
              className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm font-medium text-foreground hover:bg-secondary/80 transition-colors"
            >
              Continue with Google
            </button>
            <div className="pt-2 text-center text-xs text-muted-foreground">
              or sign in with email
            </div>
            <input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <button
              onClick={sendMagicLink}
              className="w-full rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
            >
              Send Magic Link
            </button>
            {message && (
              <p className="text-xs text-muted-foreground">{message}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (cliRedirecting) {
    return (
      <div className="flex h-screen items-center justify-center text-sm text-muted-foreground">
        Completing CLI login...
      </div>
    );
  }

  return <>{children}</>;
}
